import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Settings as SettingsIcon, LogOut } from 'lucide-react'
import { useAuth } from '../auth'
import { interactionTraces, threadChecksLatest } from '../api'
import { useProductName, FONTS } from '../productName'
import ActAsPicker from './ActAsPicker'
import SyncHealthBanner from './SyncHealthBanner'
import TweaksPanel from './TweaksPanel'

// Tab icons removed (Ken 2026-08-12) — the Studio nav is text-only for
// legibility.
const studioTabs: { to: string; label: string; end?: boolean; buildersOnly?: boolean }[] = [
  { to: '/studio',                label: 'Overview',      end: true },
  { to: '/studio/artifacts',      label: 'Artifacts'     },
  { to: '/studio/pipeline',       label: 'Pipeline'      },
  { to: '/studio/pipeline-test',  label: 'Test run'      },
  { to: '/studio/prompts',        label: 'Prompts'       },
  { to: '/studio/test-sets',      label: 'Test sets'     },
  { to: '/studio/observability',  label: 'Observability' },
  { to: '/studio/qa',             label: 'QA'            },
  { to: '/studio/control-b',      label: 'Control-B',     buildersOnly: true },
  { to: '/studio/imports',        label: 'Imports'       },
  { to: '/studio/ai-config',      label: 'AI config'     },
  { to: '/studio/claude',         label: 'AI'            },
  { to: '/studio/sync',           label: 'Sync'          },
  // Searches = end-user behavioural traces (verbatim search text + click paths).
  // Builders only (superusers + studio_editors); hidden from studio_viewer nav.
  // Placed last, right of Sync (Ken 2026-08-18).
  { to: '/studio/searches',       label: 'Search',        buildersOnly: true },
]

export default function StudioLayout() {
  const auth = useAuth()
  const user = auth.status === 'authenticated' ? auth.user : null
  const product = useProductName()

  // 'New traces' pip on the Searches tab: poll the cheap latest endpoint and
  // compare its newest timestamp to when this browser last opened Searches
  // (localStorage 'me.searches.seenAt', stamped by the Searches page). Only
  // builders can read traces, so only they poll.
  const location = useLocation()
  const canReadSearches = !!user && (user.is_superuser || user.role === 'studio_editor')
  const latestTraces = useQuery({
    queryKey: ['interactionTracesLatest'],
    queryFn: () => interactionTraces.latest(),
    enabled: canReadSearches,
    refetchInterval: 30_000,
  })
  const onSearchesPage = location.pathname.startsWith('/studio/searches')
  const searchesSeenAt = (() => { try { return localStorage.getItem('me.searches.seenAt') } catch { return null } })()
  const searchesHasNew = !onSearchesPage
    && !!latestTraces.data?.latest_at
    && (!searchesSeenAt || latestTraces.data.latest_at > searchesSeenAt)

  // Same idea for QA: pip when a new thread-issue check landed since this browser
  // last opened QA (localStorage 'me.qa.seenAt', stamped by the QA page). (Ken 2026-10-20.)
  const latestChecks = useQuery({
    queryKey: ['threadChecksLatest'],
    queryFn: () => threadChecksLatest(),
    enabled: canReadSearches,
    refetchInterval: 30_000,
  })
  const onQaPage = location.pathname.startsWith('/studio/qa')
  const qaSeenAt = (() => { try { return localStorage.getItem('me.qa.seenAt') } catch { return null } })()
  const qaHasNew = !onQaPage
    && !!latestChecks.data?.latest_at
    && (!qaSeenAt || latestChecks.data.latest_at > qaSeenAt)

  return (
    <div className="flex h-screen flex-col bg-[color:var(--me-bg)]">
      {/* Red banner across the top when workspace sync is unhealthy.
          Silent when healthy. Not shown on non-Studio pages. */}
      <SyncHealthBanner />
      <header className="flex items-center gap-1 border-b me-border bg-[color:var(--me-surface)] px-3 py-2">
        <NavLink to="/workspace" className="me-tab" title="Back to workspace">
          <ArrowLeft size={14} />
        </NavLink>
        {/* Wordmark "Studio" — same Brand font as the Workspace wordmark
            (Ken 2026-08-12). Dropped the mono "Matters" + black square. */}
        <div
          className="me-nav-wordmark mr-4 ml-2 flex items-center whitespace-nowrap leading-none"
          style={{
            fontFamily: FONTS[product.brandFont].family,
            fontSize: product.brandSize,
          }}
        >
          Studio
        </div>
        <nav className="flex flex-1 gap-0.5">
          {studioTabs.filter((t) => !t.buildersOnly || user?.is_superuser || user?.role === 'studio_editor').map(({ to, label, end }) => (
            <NavLink key={to} to={to} end={end ?? false}>
              {({ isActive }) => (
                <span data-selected={isActive ? 'true' : undefined} className="me-tab relative">
                  {label}
                  {label === 'Search' && searchesHasNew && (
                    <span
                      className="absolute -top-0.5 -right-1.5 h-1.5 w-1.5 rounded-full bg-[color:var(--me-urgent)]"
                      title="Heather / Maria have used a tracked page since you last opened Searches"
                    />
                  )}
                  {label === 'QA' && qaHasNew && (
                    <span
                      className="absolute -top-0.5 -right-1.5 h-1.5 w-1.5 rounded-full bg-[color:var(--me-urgent)]"
                      title="New thread-issue check since you last opened QA"
                    />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        {/* Same right-side cluster as the workspace header — keeps user
         *  affordances (act-as, settings, theme tweaks, logout) accessible
         *  from any Studio screen without bouncing back to /workspace. */}
        <div className="flex items-center gap-2 text-[12px] me-text-mute">
          {user && (
            <ActAsPicker user={user} onChange={() => auth.refresh()} />
          )}
          {user && (
            <span
              className="me-mono"
              title={user.acting_as
                ? `Logged in as ${user.acting_as.real_username} (superuser); acting as ${user.username}`
                : user.username}
            >
              {user.username}
              {/* Tenant code (e.g. "MOVU") — builders only (superusers +
                  studio_editors). Studio is builder-only anyway, but gate
                  it too for a uniform rule. Emerson 2026-08-07. */}
              {user.client && user.has_studio_access && (
                <span className="me-text-mute"> · {user.client.code}</span>
              )}
            </span>
          )}
          <NavLink
            to="/settings"
            className="me-btn me-btn-outline"
            title="Settings"
          >
            <SettingsIcon size={13} />
          </NavLink>
          <TweaksPanel />
          <button onClick={() => auth.logout()} className="me-btn me-btn-outline" title="Log out">
            <LogOut size={13} />
          </button>
        </div>
      </header>
      <main className="me-scroll flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
